import React, { forwardRef, useEffect, useRef, useState } from "react";

import classNames from "classnames";
import { v4 as uuidv4 } from "uuid";

import type { JSX, MouseEvent, ReactNode } from "react";

interface MultiToggleContainerProps {
  children: ReactNode;
}

export function MultiToggleContainer({
  children,
}: MultiToggleContainerProps): JSX.Element {
  const containerClasses = classNames(
    "relative flex h-[1.875rem] justify-center rounded-[0.46875rem] border border-border bg-muted",
  );

  return <div className={containerClasses}>{children}</div>;
}

interface SelectedValueIndicatorProps {
  style: { transform: string; width: number } | null;
}

export function SelectedValueIndicator({
  style,
}: SelectedValueIndicatorProps): JSX.Element | null {
  if (!style) return null;

  return (
    <div
      className="border-border-strong bg-card absolute -inset-y-px left-0 w-[1.5625rem] rounded-[0.46875rem] border drop-shadow-md transition-transform"
      style={style}
    />
  );
}

interface ValueOptionProps {
  label: string;
  onClick: (event: MouseEvent<HTMLDivElement>) => void;
  isActive?: boolean;
  isDisabled?: boolean;
  isFullWidth?: boolean;
}

export const ValueOption = forwardRef(
  (
    { label, onClick, isActive, isDisabled, isFullWidth }: ValueOptionProps,
    ref: React.Ref<HTMLDivElement>,
  ): JSX.Element => {
    const valueClassNames = classNames(
      "z-10 flex items-center justify-center rounded-[0.46875rem] px-[0.78125rem]",
      isActive
        ? "text-foreground font-medium"
        : "text-muted-foreground pb-[1px]",
      isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
      isFullWidth && "flex-1",
    );

    const handleClick = (event: MouseEvent<HTMLDivElement>): void => {
      if (isDisabled) return;
      onClick(event);
    };

    return (
      <div ref={ref} className={valueClassNames} onClick={handleClick}>
        {label}
      </div>
    );
  },
);

ValueOption.displayName = "ValueOption";

interface MultiToggleProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: { label: string; value: T }[];
  isLoading?: boolean;
  isFullWidth?: boolean;
}

function MultiToggle<T extends string>({
  value,
  onChange,
  options,
  isLoading,
  isFullWidth,
}: MultiToggleProps<T>): JSX.Element {
  const [selectedPosition, setSelectedPosition] = useState<null | {
    transform: string;
    width: number;
  }>(null);
  const [selectedValue, setSelectedValue] = useState<T | null>(null);

  const valueOptionRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const defaultViewIndex = options.findIndex(
      (option) => option.value === value,
    );
    const currentElement = valueOptionRef.current[defaultViewIndex];

    if (!currentElement || !value) return;

    setSelectedPosition({
      transform: `translateX(${currentElement?.offsetLeft}px)`,
      width: currentElement?.offsetWidth || 0,
    });

    setSelectedValue(value);
  }, [value, options]);

  const handleClick = (currentValue: T, index: number): void => {
    const currentElement = valueOptionRef.current[index];

    setSelectedPosition({
      transform: `translateX(${Math.floor(currentElement?.offsetLeft ?? 0)}px)`,
      width: Math.floor(currentElement?.offsetWidth ?? 0),
    });

    setSelectedValue(currentValue);

    onChange(currentValue);
  };

  return (
    <MultiToggleContainer>
      <SelectedValueIndicator style={selectedPosition} />
      {options.map((option, index) => (
        <ValueOption
          key={uuidv4()}
          ref={(ref): void => {
            valueOptionRef.current[index] = ref;
          }}
          isActive={selectedValue === option.value}
          isDisabled={isLoading}
          isFullWidth={isFullWidth}
          label={option.label}
          onClick={(): void => handleClick(option.value, index)}
        />
      ))}
    </MultiToggleContainer>
  );
}

export default MultiToggle;
